/** The tenant's MCP tab (client MCP plan §9 P0, Q26; WPS FE-02): ops levers for incidents, not
 *  commercial gates.
 *
 *  - **Tool domains** (`/mcp_switches/`): the tenant's own kill switch per tool domain, and whether
 *    the domain is on in effect — off when the tenant's switch, its franchise parent's (a licensee
 *    inherits it; switched on on the parent's tab) or the platform's (`DjangoMCPDisabledDomains`)
 *    is off. A domain switched off lists none of its tools for the tenant and refuses them; applying
 *    or reverting its changes is refused. Saving sends only the domains toggled here, so two
 *    operators switching different domains never undo each other. Viewing needs `console_top_view`;
 *    switching `console_mcp_tenant_switches` (`can_edit`) and a reason.
 *  - **Auto-apply** (`/ai_edit/`): its state, and the ops lock that keeps it off — here and on the
 *    tenant's dashboard, where admins holding `ai_edit.manage` may otherwise turn it on. Locking
 *    turns it off in the same save; unlocking leaves it off. The switch itself is on the AI editing
 *    tab. Viewing needs `console_ai_changes_view`; locking `console_mcp_tenant_switches`
 *    (`can_lock`) and a reason.
 *
 *  Both saves write the tenant's config (Configuration → Change history, with the reason) and the
 *  console audit log; a change of the lock also shows in the tenant's dashboard Activity log. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  aiEditKeys,
  aiEditSettings,
  updateAIEditSettings,
  type TourOperatorAIEditSettings,
} from '@/api/aiEdit';
import {
  MCP_SWITCHES_AUDIT_EVENT,
  MCP_SWITCHES_REASON_MAX,
  mcpSwitches,
  mcpSwitchesErrorText,
  mcpSwitchesKeys,
  updateMcpSwitches,
  type TourOperatorMcpSwitchDomain,
  type TourOperatorMcpSwitches,
} from '@/api/mcpSwitches';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { Field, SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { auditHref } from '@/utils/gridLinks';

/** The console audit log's event for a change of the AI-editing settings, the lock included. */
const AI_EDIT_AUDIT_EVENT = 'ai_edit.settings_changed';

type Parent = Pick<TourOperatorDetail, 'parent_id' | 'parent_name' | 'parent_code'>;

export function McpTab({ to }: { to: TourOperatorDetail }) {
  const { hasPerm } = usePermissions();
  return (
    <>
      <DomainsSection topId={to.id} parent={to} />
      {hasPerm('console_ai_changes_view') ? (
        <AutoApplySection topId={to.id} />
      ) : (
        <SectionCard title="MCP auto-apply">
          <Box data-mcp-auto-apply>
            <Typography variant="body2" color="text.secondary">
              Auto-apply and its lock need the console_ai_changes_view permission.
            </Typography>
          </Box>
        </SectionCard>
      )}
    </>
  );
}

/** A save wrote the tenant's config: its change history and the record the Configuration tab
 *  edits are stale now. */
function useConfigSaved(topId: number) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['tourOperators', topId, 'config_changes'] });
    void queryClient.invalidateQueries({ queryKey: ['tourOperators', 'detail', topId] });
  };
}

/** Where a change is audited: the tenant's config history and the console audit log. */
function AuditHint({ topId, event, children }: { topId: number; event: string; children?: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" data-audit-hint>
      Every change is audited: Configuration →{' '}
      <Link component={RouterLink} to={`/tour-operators/${topId}/configuration/history`}>
        Change history
      </Link>{' '}
      keeps it with its reason, and the console{' '}
      <Link component={RouterLink} to={auditHref({ event_contains: event })}>
        audit log
      </Link>{' '}
      records it as <code>{event}</code>
      {children}.
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Tool domains
// ---------------------------------------------------------------------------

function DomainsSection({ topId, parent }: { topId: number; parent: Parent }) {
  const query = useQuery({
    queryKey: mcpSwitchesKeys.switches(topId),
    queryFn: () => mcpSwitches(topId),
  });
  return (
    <SectionCard title="MCP tool domains">
      <Box data-mcp-domains>
        {query.isPending ? (
          <CircularProgress />
        ) : query.isError ? (
          <Alert severity="error">Couldn’t load the MCP switches: {query.error.message}</Alert>
        ) : (
          <DomainsForm switches={query.data} parent={parent} />
        )}
      </Box>
    </SectionCard>
  );
}

function DomainsForm({ switches, parent }: { switches: TourOperatorMcpSwitches; parent: Parent }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const topId = switches.tour_operator_id;
  const configSaved = useConfigSaved(topId);
  // Only the domains toggled here, over the saved state: a save sends just these, and a domain
  // another operator switched meanwhile shows as saved.
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const changes = switches.domains.filter(
    (d) => edits[d.domain] !== undefined && edits[d.domain] !== d.tenant_enabled,
  );
  const dirty = changes.length > 0;

  const mutation = useMutation({
    mutationFn: () =>
      updateMcpSwitches(topId, {
        domains: Object.fromEntries(changes.map((d) => [d.domain, !d.tenant_enabled])),
        reason,
      }),
    onSuccess: ({ changed, ...next }) => {
      queryClient.setQueryData(mcpSwitchesKeys.switches(topId), next);
      setEdits({});
      setReason('');
      const any = Object.keys(changed).length > 0;
      if (any) configSaved();
      snackbar.show(
        any
          ? 'Saved — the change is in the config history. It takes effect on the next MCP request.'
          : 'Nothing to save: the switches were already set.',
        any ? 'success' : 'info',
      );
    },
  });

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        A domain switched off lists none of its tools for this tenant and refuses them, on every MCP
        connection and in the console agent; applying or reverting its changes is refused. It takes
        effect on the next MCP request.
      </Typography>
      <Table size="small" aria-label="MCP tool domains">
        <TableHead>
          <TableRow>
            <TableCell>Tool domain</TableCell>
            <TableCell>This tenant</TableCell>
            <TableCell>In effect</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {switches.domains.map((d) => (
            <TableRow key={d.domain} data-domain={d.domain}>
              <TableCell>
                <Typography variant="body2">{d.description || d.domain}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {d.domain}
                </Typography>
              </TableCell>
              <TableCell>
                <Switch
                  checked={edits[d.domain] ?? d.tenant_enabled}
                  disabled={!switches.can_edit || mutation.isPending}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [d.domain]: e.target.checked }))}
                  inputProps={{ 'aria-label': `${d.description || d.domain} (${d.domain})` }}
                />
              </TableCell>
              <TableCell>
                <InEffect domain={d} parent={parent} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box data-disabled-tools>
        <Field
          label="Tools switched off platform-wide (DjangoMCPDisabledTools)"
          value={switches.disabled_tools.length ? switches.disabled_tools.join(', ') : 'None'}
        />
      </Box>
      {switches.can_edit ? (
        <>
          {dirty && (
            <Typography variant="body2" data-pending-summary>
              On save: {changes.map((d) => `${d.domain} ${d.tenant_enabled ? 'off' : 'on'}`).join(', ')}.
            </Typography>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: MCP_SWITCHES_REASON_MAX }}
            helperText="Why the switches change, e.g. the incident."
          />
          {mutation.isError && <Alert severity="error">{mcpSwitchesErrorText(mutation.error)}</Alert>}
          <Box>
            <Button
              variant="contained"
              disabled={!dirty || !reason.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Save
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Switching needs the console_mcp_tenant_switches permission.
        </Typography>
      )}
      <AuditHint topId={topId} event={MCP_SWITCHES_AUDIT_EVENT} />
    </Stack>
  );
}

/** On, or Off with where it is switched off: for this tenant, on its franchise parent, platform-wide. */
function InEffect({ domain: d, parent }: { domain: TourOperatorMcpSwitchDomain; parent: Parent }) {
  if (d.enabled) return <Chip size="small" color="success" label="On" />;
  const parentName = parent.parent_name ?? parent.parent_code ?? 'the franchise parent';
  const where: ReactNode[] = [];
  if (!d.tenant_enabled) where.push('for this tenant');
  if (!d.parent_enabled) {
    where.push(
      <>
        on the franchise parent
        {parent.parent_id != null && (
          <>
            {' ('}
            <Link component={RouterLink} to={`/tour-operators/${parent.parent_id}/mcp`}>
              {parentName}’s MCP tab
            </Link>
            )
          </>
        )}
      </>,
    );
  }
  if (!d.globally_enabled) where.push('platform-wide (DjangoMCPDisabledDomains)');
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip size="small" label="Off" />
      <Typography variant="body2" color="text.secondary" data-off-where>
        {where.map((w, i) => (
          <span key={i}>
            {i > 0 && '; '}
            {w}
          </span>
        ))}
      </Typography>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Auto-apply and its ops lock
// ---------------------------------------------------------------------------

function AutoApplySection({ topId }: { topId: number }) {
  // The AI editing tab's query: a lock saved here shows there at once.
  const query = useQuery({
    queryKey: aiEditKeys.settings(topId),
    queryFn: () => aiEditSettings(topId),
  });
  return (
    <SectionCard title="MCP auto-apply">
      <Box data-mcp-auto-apply>
        {query.isPending ? (
          <CircularProgress />
        ) : query.isError ? (
          <Alert severity="error">Couldn’t load the auto-apply settings: {query.error.message}</Alert>
        ) : (
          <LockForm settings={query.data} />
        )}
      </Box>
    </SectionCard>
  );
}

function autoApplyState(settings: TourOperatorAIEditSettings): string {
  if (settings.mcp_auto_apply_locked) {
    return (
      'Locked off — production MCP changes wait for an approver, unless the proposer may approve ' +
      'them all; neither ops nor the tenant’s admins can turn auto-apply on until it is unlocked.'
    );
  }
  if (settings.mcp_auto_apply) {
    return 'On — production MCP changes apply directly when the sign-in proved a second factor (MFA or SSO).';
  }
  return 'Off — production MCP changes wait for an approver, unless the proposer may approve them all.';
}

function LockForm({ settings }: { settings: TourOperatorAIEditSettings }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const topId = settings.tour_operator_id;
  const configSaved = useConfigSaved(topId);
  const [locked, setLocked] = useState(settings.mcp_auto_apply_locked);
  const [reason, setReason] = useState('');
  const dirty = locked !== settings.mcp_auto_apply_locked;

  const mutation = useMutation({
    mutationFn: () => updateAIEditSettings(topId, { mcp_auto_apply_locked: locked, reason }),
    onSuccess: (next) => {
      queryClient.setQueryData(aiEditKeys.settings(topId), next);
      configSaved();
      setLocked(next.mcp_auto_apply_locked);
      setReason('');
      snackbar.show('Saved — the change is in the config history.', 'success');
    },
  });

  return (
    <Stack spacing={2}>
      <Field label="Auto-apply" value={autoApplyState(settings)} />
      {settings.environment === 'sandbox' && (
        <Typography variant="body2" color="text.secondary">
          A sandbox applies MCP changes directly anyway.
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Auto-apply itself is switched on the{' '}
        <Link component={RouterLink} to={`/tour-operators/${topId}/ai-editing`}>
          AI editing tab
        </Link>{' '}
        (ops) or on the tenant’s dashboard (its admins holding ai_edit.manage).
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={locked}
            disabled={!settings.can_lock || mutation.isPending}
            onChange={(e) => setLocked(e.target.checked)}
            inputProps={{ 'aria-label': 'Lock auto-apply off' }}
          />
        }
        label="Lock auto-apply off — keeps it off, here and on the tenant’s dashboard, until ops unlock it"
      />
      {dirty && locked && settings.mcp_auto_apply && (
        <Alert severity="warning" data-lock-warning>
          Locking turns auto-apply off now: production MCP changes then wait for an approver.
        </Alert>
      )}
      {dirty && !locked && (
        <Alert severity="info">
          Unlocking leaves auto-apply off; ops or the tenant’s admins can then turn it on.
        </Alert>
      )}
      {settings.can_lock ? (
        <>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: MCP_SWITCHES_REASON_MAX }}
            helperText="Why auto-apply is locked or unlocked."
          />
          {mutation.isError && <Alert severity="error">{mcpSwitchesErrorText(mutation.error)}</Alert>}
          <Box>
            <Button
              variant="contained"
              disabled={!dirty || !reason.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Save
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Locking needs the console_mcp_tenant_switches permission.
        </Typography>
      )}
      <AuditHint topId={topId} event={AI_EDIT_AUDIT_EVENT}>
        ; the tenant’s dashboard Activity log shows it
      </AuditHint>
    </Stack>
  );
}
